import { useState, useEffect, useCallback } from 'react';
import { getPrompts, getPromotionPolicies, getEvaluationRuns } from '../services/aiOperationsApi';
import { listDatasets } from '../services/datasetsApi';
import { listAPIKeys } from '../services/apiKeysApi';

export interface OnboardingStep {
  id: string;
  label: string;
  description: string;
  route: string;
  section: string;
}

/**
 * Onboarding steps for GenAIOps
 * Each step has:
 * - id: unique identifier
 * - label: display name
 * - description: what this step accomplishes
 * - route: where to go to complete this step
 * - check: function to determine if step is complete
 */
export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'api_key',
    label: 'Create API Key',
    description: 'Generate an API key to authenticate your agents with the SDK',
    route: '/ai-operations/api-keys',
    section: 'settings'
  },
  {
    id: 'llm_provider',
    label: 'Configure LLM Provider',
    description: 'Set up your LLM providers (OpenAI, Anthropic, Bedrock)',
    route: '/ai-operations/llm-providers',
    section: 'settings'
  },
  {
    id: 'first_prompt',
    label: 'Create First Prompt',
    description: 'Create a prompt template with variables for your agents to use',
    route: '/ai-operations/registry?tab=prompts',
    section: 'registry'
  },
  {
    id: 'first_dataset',
    label: 'Create Dataset',
    description: 'Build a test dataset with input/output pairs for evaluation',
    route: '/ai-operations/registry?tab=datasets',
    section: 'registry'
  },
  {
    id: 'first_eval',
    label: 'Run Evaluation',
    description: 'Test your prompt against your dataset to measure quality',
    route: '/ai-operations/evaluation?tab=runs',
    section: 'evaluation'
  },
  {
    id: 'first_policy',
    label: 'Set Up Promotion Policy',
    description: 'Define quality gates for promoting prompts to production',
    route: '/ai-operations/governance?tab=policies',
    section: 'governance'
  },
  {
    id: 'agent_run',
    label: 'Execute Agent',
    description: 'Run your first agent using the SDK with telemetry',
    route: '/ai-operations/runs',
    section: 'agentops'
  }
];

interface OnboardingStatus {
  hasApiKey: boolean;
  hasLLMConfig: boolean;
  hasPrompt: boolean;
  hasDataset: boolean;
  hasEvaluation: boolean;
  hasPolicy: boolean;
  hasAgentRun: boolean;
  loading: boolean;
  error: string | null;
}

type StepStatusMap = Record<string, boolean>;

interface UseOnboardingStatusReturn extends OnboardingStatus {
  stepStatusMap: StepStatusMap;
  completedSteps: number;
  totalSteps: number;
  completionPercentage: number;
  nextStep: OnboardingStep | undefined;
  isComplete: boolean;
  refresh: () => Promise<void>;
}

/**
 * Hook to track onboarding progress
 * Checks various APIs to determine which steps are complete
 */
export const useOnboardingStatus = (): UseOnboardingStatusReturn => {
  const [status, setStatus] = useState<OnboardingStatus>({
    hasApiKey: false,
    hasLLMConfig: false,
    hasPrompt: false,
    hasDataset: false,
    hasEvaluation: false,
    hasPolicy: false,
    hasAgentRun: false,
    loading: true,
    error: null
  });

  const checkStatus = useCallback(async () => {
    try {
      // Check all statuses in parallel
      const [apiKeys, prompts, datasets, policies, evalRuns] = await Promise.allSettled([
        listAPIKeys().catch(() => []),
        getPrompts({ limit: 1 } as any).catch(() => ({ data: [] })),
        listDatasets({ limit: 1 } as any).catch(() => ({ data: [] })),
        getPromotionPolicies({ limit: 1 } as any).catch(() => ({ data: [] })),
        getEvaluationRuns({ limit: 1 } as any).catch(() => ({ data: [] }))
      ]);

      const getDataLength = (result: PromiseSettledResult<any>): number => {
        if (result.status === 'rejected') return 0;
        const data = result.value;
        // Handle different response formats
        if (Array.isArray(data)) return data.length;
        return data?.data?.length || data?.prompts?.length || data?.runs?.length || 0;
      };

      // Check localStorage-tracked steps
      const hasLLMConfig = localStorage.getItem('genaiops_configured_llm') === 'true';
      const hasAgentRun = localStorage.getItem('genaiops_agent_run') === 'true';

      setStatus({
        hasApiKey: getDataLength(apiKeys) > 0,
        hasLLMConfig,
        hasPrompt: getDataLength(prompts) > 0,
        hasDataset: getDataLength(datasets) > 0,
        hasEvaluation: getDataLength(evalRuns) > 0,
        hasPolicy: getDataLength(policies) > 0,
        hasAgentRun,
        loading: false,
        error: null
      });
    } catch (err) {
      console.error('Error checking onboarding status:', err);
      setStatus(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : String(err)
      }));
    }
  }, []);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Calculate completion
  const completedSteps = [
    status.hasApiKey,
    status.hasLLMConfig,
    status.hasPrompt,
    status.hasDataset,
    status.hasEvaluation,
    status.hasPolicy,
    status.hasAgentRun
  ].filter(Boolean).length;

  const totalSteps = ONBOARDING_STEPS.length;
  const completionPercentage = Math.round((completedSteps / totalSteps) * 100);

  // Find next incomplete step
  const stepStatusMap: StepStatusMap = {
    api_key: status.hasApiKey,
    llm_provider: status.hasLLMConfig,
    first_prompt: status.hasPrompt,
    first_dataset: status.hasDataset,
    first_eval: status.hasEvaluation,
    first_policy: status.hasPolicy,
    agent_run: status.hasAgentRun
  };

  const nextStep = ONBOARDING_STEPS.find(step => !stepStatusMap[step.id]);
  const isComplete = completedSteps === totalSteps;

  return {
    ...status,
    stepStatusMap,
    completedSteps,
    totalSteps,
    completionPercentage,
    nextStep,
    isComplete,
    refresh: checkStatus
  };
};

// Helper function to mark a step as complete (for page views)
export const markGenAIOpsStepComplete = (stepId: string): void => {
  const stepKeys: Record<string, string> = {
    llm_provider: 'genaiops_configured_llm',
    agent_run: 'genaiops_agent_run'
  };

  const key = stepKeys[stepId];
  if (key) {
    localStorage.setItem(key, 'true');
  }
};

export default useOnboardingStatus;
